import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * The parts around a field: its label, the help behind a "?" button, a line
 * of description, and its error (ADR-0088 D2). Admin composed these in
 * `src/common/`; Student's identity form had its own in `AccInput`.
 *
 * The size follows the field beside it. `default` and `compact` are Admin's
 * dense labels; `lg` is the customer label of Student's sign-in, activation
 * and password-recovery pages, paired with an `lg` field.
 *
 * Wiring stays at the call site, because only it knows the ids: a label
 * names its control with `htmlFor`, and a description or error is linked
 * with the field's `aria-describedby` and marks it `aria-invalid`.
 */
import { Icon } from './icon.js';
import { Button } from './button.js';
import { cn } from './cn.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
const labelText = {
    default: 'text-body font-medium',
    /** Compact labels sit below a fieldset legend instead of competing with it. */
    compact: 'text-label font-medium',
    lg: 'text-body font-semibold text-text-secondary',
};
/** A form label and its optional help, with one scale per field shape. */
export function FieldLabel({ htmlFor, label, icon, help, helpLabel = label.toLowerCase(), size = 'default', }) {
    const className = labelText[size];
    const labelContent = icon === undefined ? (label) : (_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [icon, label] }));
    return (_jsxs("div", { className: cn('flex items-center gap-1', size === 'lg' ? 'mb-2' : 'mb-1'), children: [htmlFor === undefined ? (_jsx("span", { className: className, children: labelContent })) : (_jsx("label", { htmlFor: htmlFor, className: className, children: labelContent })), help !== undefined && (_jsx(FieldHelp, { label: helpLabel, size: "compact", children: help }))] }));
}
/**
 * Help a field needs only once: a "?" button that opens a popover.
 *
 * A popover rather than hover-only help, so the explanation reaches keyboard
 * and touch users and stays open while they choose a value. Admin's
 * conventions keep this guidance out of inline prose under the field.
 */
export function FieldHelp({ label, children, size = 'default', }) {
    return (_jsxs(Popover, { children: [_jsx(PopoverTrigger, { render: _jsx(Button, { variant: "ghost", size: "icon", "aria-label": `About ${label}`, className: cn(size === 'compact' ? 'size-5' : 'size-6', 'shrink-0 text-text-secondary') }), children: _jsx(Icon, { name: "help", size: 14 }) }), _jsx(PopoverContent, { "data-slot": "field-help", className: "w-72 p-3 text-caption", children: children })] }));
}
/**
 * A line under a customer field that says what the value is for. Link it
 * with the field's `aria-describedby`.
 */
export function FieldDescription({ id, children }) {
    return (_jsx("p", { id: id, "data-slot": "field-description", className: "mt-1.5 text-caption text-text-secondary", children: children }));
}
const errorText = {
    default: 'mt-1 text-caption',
    lg: 'mt-1.5 text-caption',
};
/**
 * The message under the field that caused it. `role="alert"`, so it is
 * announced when it appears. Renders nothing for `null`, so a form passes
 * every field its message and only the failing one speaks. Link it with the
 * field's `aria-describedby` and set `aria-invalid` on the field.
 *
 * The text is the theme's error-text color, `danger-text`: `--color-danger` as
 * text on white is 4.07:1, under AA's 4.5:1.
 */
export function FieldError({ message, id, size = 'default', }) {
    if (message === null)
        return null;
    return (_jsx("p", { id: id, role: "alert", "data-slot": "field-error", className: cn(errorText[size], 'text-danger-text'), children: message }));
}
//# sourceMappingURL=field.js.map